import {
  Button,
  Kbd,
  Loader,
  Modal,
  SimpleGrid,
  Text,
  Title,
} from "@mantine/core";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { PlayerButton } from "./PlayerButton";
import { usePlayerListByTeam } from "../../../../hooks/usePlayerListByTeam.ts";

interface TeamPlayerModalProps {
  teamId: string;
  opened: boolean;
  close: () => void;
  onClick: (playerId?: string) => void;
  showEmptyPlayer?: boolean;
  emptyPlayerText?: ReactNode;
  emptyPlayerAction?: () => void;
  secondaryEmptyPlayerText?: ReactNode;
  secondaryEmptyPlayerAction?: () => void;
  headline: ReactNode;
  buttonColor: string;
  disabledPlayerIds?: string[];
  allDisabled: boolean;
}

export const TeamPlayerModal: FC<TeamPlayerModalProps> = (props) => {
  const {
    teamId,
    opened,
    close,
    onClick,
    showEmptyPlayer,
    emptyPlayerText,
    emptyPlayerAction,
    secondaryEmptyPlayerText,
    secondaryEmptyPlayerAction,
    headline,
    buttonColor,
    disabledPlayerIds,
    allDisabled,
  } = props;

  const players = usePlayerListByTeam(teamId);

  // Keyboard input for jersey numbers
  const [typedNumber, setTypedNumber] = useState("");

  const selectPlayerByNumber = useCallback(
    (number: number) => {
      if (!players.isSuccess || allDisabled) return;

      const player = players.data.find((p) => p.number === number);
      if (player && !disabledPlayerIds?.includes(player.id)) {
        onClick(player.id);
      }
    },
    [players.isSuccess, players.data, allDisabled, disabledPlayerIds, onClick],
  );

  // Reset typed number when modal closes
  useEffect(() => {
    if (!opened) {
      setTypedNumber("");
    }
  }, [opened]);

  // Handle keyboard input
  useEffect(() => {
    if (!opened) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key repeats (holding down key)
      if (e.repeat) return;

      // Handle digit keys
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        setTypedNumber((prev) => prev + e.key);
        return;
      }

      // Handle Enter to confirm selection
      if (e.key === "Enter" && typedNumber) {
        e.preventDefault();
        const number = parseInt(typedNumber, 10);
        if (!isNaN(number)) {
          selectPlayerByNumber(number);
        }
        setTypedNumber("");
        return;
      }

      // Handle Backspace to correct input
      if (e.key === "Backspace" && typedNumber) {
        e.preventDefault();
        setTypedNumber((prev) => prev.slice(0, -1));
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [opened, typedNumber, selectPlayerByNumber]);

  return (
    <Modal
      size="xl"
      opened={opened}
      onClose={close}
      centered
      closeOnClickOutside={false}
      closeOnEscape={true}
      trapFocus={true}
      withCloseButton={false}
    >
      <Title mb="md">{headline}</Title>
      <Text size="sm" c="dimmed" mb="md">
        Trikotnummer + <Kbd>Enter</Kbd>:{" "}
        <Kbd style={{ minWidth: 40, textAlign: "center" }}>
          {typedNumber || "—"}
        </Kbd>
      </Text>
      <SimpleGrid cols={5} spacing="xl">
        {!players.isSuccess ? (
          <Loader />
        ) : (
          <>
            {players.data.map((p) => (
              <PlayerButton
                key={p.id}
                onClick={() => {
                  onClick(p.id);
                }}
                number={p.number}
                color={buttonColor}
                disabled={!!disabledPlayerIds?.includes(p.id) || allDisabled}
              />
            ))}
            {showEmptyPlayer && (
              <Button
                fullWidth
                color="red"
                disabled={allDisabled}
                onClick={() => {
                  emptyPlayerAction ? emptyPlayerAction() : onClick();
                }}
                styles={() => ({
                  root: {
                    height: 100,
                    fontSize: 20,
                    textAlign: "center",
                    lineHeight: "1.3em",
                  },
                })}
              >
                {emptyPlayerText ?? "kein Spieler"}
              </Button>
            )}
            {secondaryEmptyPlayerAction && secondaryEmptyPlayerText && (
              <Button
                fullWidth
                color="gray"
                disabled={allDisabled}
                onClick={secondaryEmptyPlayerAction}
                styles={() => ({
                  root: {
                    height: 100,
                    fontSize: 20,
                    textAlign: "center",
                    lineHeight: "1.3em",
                  },
                })}
              >
                {secondaryEmptyPlayerText}
              </Button>
            )}
          </>
        )}
      </SimpleGrid>
      <Button mt="xl" color="red" variant="outline" onClick={close}>
        Abbrechen
      </Button>
    </Modal>
  );
};
